// Dashboard.jsx
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import StatsCards from './StatsCards';
import RecentTickets from './RecentTickets';
import Charts from './Charts';
import { apiHelper } from '@services/api';
import Loading, { CardSkeleton } from '@components/common/Loading';

const Dashboard = () => {
  // Fetch tickets data and calculate stats
  const {
    data: tickets,
    isLoading,
    error
  } = useQuery(
    ['dashboard-tickets'],
    async () => {
      const response = await apiHelper.get('/tickets');
      return response.data;
    },
    {
      refetchInterval: 60000, // Refetch every minute
      staleTime: 30000 // Consider data stale after 30 seconds
    }
  );

  // Calculate dashboard stats from tickets
  const dashboardData = tickets ? {
    stats: {
      totalTickets: tickets.length,
      openTickets: tickets.filter(t => ['not_started', 'in_progress', 'escalated'].includes(t.status)).length,
      closedTickets: tickets.filter(t => t.status === 'closed').length,
      criticalTickets: tickets.filter(t => t.priority === 'critical').length,
      trends: {
        totalTickets: 0,
        openTickets: 0,
        closedTickets: 0,
        criticalTickets: 0
      }
    },
    recentTickets: tickets.slice(0, 5).map(ticket => ({
      id: ticket.id,
      ticketNumber: ticket.ticket_number,
      subject: ticket.subject,
      priority: ticket.priority,
      status: ticket.status,
      createdAt: ticket.created_at,
      assignedTo: ticket.assigned_to
    }))
  } : null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 12 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.32 }
    }
  };

  // Inline style objects to match Login / ForgotPassword visual language
  const styles = {
    page: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem'
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: '1rem',
      padding: '1.25rem',
      border: '1px solid #eef2f6',
      boxShadow: '0 10px 30px rgba(2,6,23,0.04)'
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      marginBottom: '0.75rem'
    },
    cardTitle: {
      margin: 0,
      fontSize: '1.125rem',
      fontWeight: 700,
      color: 'var(--gray-900)'
    },
    cardSub: {
      margin: 0,
      fontSize: '0.875rem',
      color: 'var(--gray-600)'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '1rem'
    },
    gridTwo: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '1rem'
    },
    quickActionBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      width: '100%',
      padding: '0.75rem',
      borderRadius: '0.75rem',
      background: 'var(--primary-50)',
      border: '1px solid transparent',
      cursor: 'pointer',
      transition: 'transform 120ms ease, background-color 120ms ease'
    },
    quickActionIconBox: {
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--primary-600)',
      color: 'white'
    },
    quickActionSecondaryIconBox: {
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--gray-600)',
      color: 'white'
    },
    quickActionGreenBox: {
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--green-600)',
      color: 'white'
    },
    teamMemberRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.75rem'
    },
    avatarCircle: {
      width: '2rem',
      height: '2rem',
      borderRadius: '9999px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--gray-200)'
    },
    emptyStateIconBox: {
      width: '4rem',
      height: '4rem',
      borderRadius: '9999px',
      background: 'var(--error-100)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 0.75rem auto'
    }
  };

  if (error) {
    return (
      <motion.div
        style={{
          ...styles.card,
          textAlign: 'center'
        }}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div style={styles.emptyStateIconBox}>
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            style={{ color: 'var(--error-600)' }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M12 8v4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 16h.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M21 12A9 9 0 1 1 3 12a9 9 0 0 1 18 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--gray-900)', margin: '0 0 0.5rem 0' }}>
          Failed to load dashboard
        </h3>
        <p style={{ color: 'var(--gray-600)', marginBottom: '1rem' }}>
          There was an error loading your dashboard data.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.625rem 1rem',
            background: 'var(--primary-600)',
            color: 'white',
            borderRadius: '0.5rem',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </motion.div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Stats Cards Skeleton */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem' }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <CardSkeleton key={index} style={{ height: 128 }} />
          ))}
        </div>

        {/* Charts Skeleton */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <CardSkeleton style={{ height: 320 }} />
          <CardSkeleton style={{ height: 320 }} />
        </div>

        {/* Recent Tickets Skeleton */}
        <CardSkeleton style={{ height: 400 }} />
      </div>
    );
  }

  return (
    <motion.div
      style={styles.page}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Stats Cards */}
      <motion.div variants={itemVariants}>
        <div style={{ ...styles.card, padding: 0 }}>
          {/* let the StatsCards component render its own internal layout.
              We keep the white card background and padding consistent by wrapping */}
          <div style={{ padding: '1rem' }}>
            <StatsCards data={dashboardData?.stats} />
          </div>
        </div>
      </motion.div>

      {/* Charts */}
      <motion.div variants={itemVariants}>
        <div style={styles.card}>
          <Charts data={dashboardData?.charts} />
        </div>
      </motion.div>

      {/* Recent Activity / Layout */}
      <motion.div variants={itemVariants}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem' }}>
          {/* Recent Tickets */}
          <div>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Recent Tickets</h3>
              <p style={{ ...styles.cardSub, marginBottom: '0.75rem' }}>
                Latest support requests and updates
              </p>
              <RecentTickets data={dashboardData?.recentTickets} />
            </div>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Quick Actions */}
            <motion.div style={styles.card} whileHover={{ y: -4 }} transition={{ duration: 0.18 }}>
              <div style={styles.cardHeader}>
                <div>
                  <h3 style={styles.cardTitle}>Quick Actions</h3>
                  <p style={styles.cardSub}>Common tasks to get you started</p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button
                  style={styles.quickActionBtn}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-3px)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
                  onClick={() => (window.location.href = '/tickets/create')}
                >
                  <div style={styles.quickActionIconBox}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'white' }}>
                      <path d="M12 4v16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M20 12H4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, color: 'var(--gray-900)' }}>Create Ticket</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>New support request</div>
                  </div>
                </button>

                <button
                  style={{ ...styles.quickActionBtn, background: 'var(--gray-50)' }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-3px)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
                  onClick={() => (window.location.href = '/tickets')}
                >
                  <div style={styles.quickActionSecondaryIconBox}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'white' }}>
                      <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, color: 'var(--gray-900)' }}>View All Tickets</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Manage requests</div>
                  </div>
                </button>

                <button
                  style={{ ...styles.quickActionBtn, background: 'rgba(16,185,129,0.06)' }}
                  onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-3px)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'translateY(0)')}
                  onClick={() => (window.location.href = '/reports')}
                >
                  <div style={styles.quickActionGreenBox}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ color: 'white' }}>
                      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9 19V9a2 2 0 012-2h2a2 2 0 012 2v10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, color: 'var(--gray-900)' }}>View Reports</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Analytics & insights</div>
                  </div>
                </button>
              </div>
            </motion.div>

            {/* Team Performance */}
            <motion.div style={styles.card} whileHover={{ y: -4 }} transition={{ duration: 0.18 }}>
              <div style={styles.cardHeader}>
                <div>
                  <h3 style={styles.cardTitle}>Team Performance</h3>
                  <p style={styles.cardSub}>Resolved tickets by team members</p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {dashboardData?.teamPerformance && dashboardData.teamPerformance.length > 0 ? (
                  dashboardData.teamPerformance.map((member, i) => (
                    <div key={i} style={styles.teamMemberRow}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={styles.avatarCircle}>
                          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--gray-700)' }}>
                            {member.name
                              .split(' ')
                              .map(n => n[0])
                              .join('')
                              .slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--gray-900)' }}>{member.name}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{member.role}</div>
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, color: 'var(--gray-900)' }}>{member.resolvedTickets}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>resolved</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                    <div style={{ color: 'var(--gray-500)' }}>No team data available</div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Dashboard;
